import React, { useEffect, useState, createContext, useContext } from 'react';
interface CurrencyInfo {
  code: string;
  symbol: string;
  rate: number;
  locale: string;
  name: string;
}
const CURRENCIES: Record<string, CurrencyInfo> = {
  USD: {
    code: 'USD',
    symbol: '$',
    rate: 1,
    locale: 'en-US',
    name: 'US Dollar'
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    rate: 0.92,
    locale: 'de-DE',
    name: 'Euro'
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    rate: 0.79,
    locale: 'en-GB',
    name: 'British Pound'
  },
  CAD: {
    code: 'CAD',
    symbol: 'CA$',
    rate: 1.36,
    locale: 'en-CA',
    name: 'Canadian Dollar'
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    rate: 1.53,
    locale: 'en-AU',
    name: 'Australian Dollar'
  },
  MXN: {
    code: 'MXN',
    symbol: 'MX$',
    rate: 17.15,
    locale: 'es-MX',
    name: 'Mexican Peso'
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    rate: 4.97,
    locale: 'pt-BR',
    name: 'Brazilian Real'
  },
  COP: {
    code: 'COP',
    symbol: 'COL$',
    rate: 3950,
    locale: 'es-CO',
    name: 'Colombian Peso'
  },
  ARS: {
    code: 'ARS',
    symbol: 'AR$',
    rate: 875,
    locale: 'es-AR',
    name: 'Argentine Peso'
  },
  CLP: {
    code: 'CLP',
    symbol: 'CL$',
    rate: 940,
    locale: 'es-CL',
    name: 'Chilean Peso'
  },
  PEN: {
    code: 'PEN',
    symbol: 'S/',
    rate: 3.72,
    locale: 'es-PE',
    name: 'Peruvian Sol'
  }
};
const COUNTRY_TO_CURRENCY: Record<string, string> = {
  US: 'USD',
  PR: 'USD',
  GU: 'USD',
  GB: 'GBP',
  UK: 'GBP',
  DE: 'EUR',
  FR: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  PT: 'EUR',
  IE: 'EUR',
  FI: 'EUR',
  GR: 'EUR',
  CA: 'CAD',
  AU: 'AUD',
  NZ: 'AUD',
  MX: 'MXN',
  BR: 'BRL',
  CO: 'COP',
  AR: 'ARS',
  CL: 'CLP',
  PE: 'PEN'
};
const TZ_COUNTRY_MAP: Record<string, string> = {
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Edmonton': 'CA',
  'America/Winnipeg': 'CA',
  'America/Halifax': 'CA',
  'America/St_Johns': 'CA',
  'America/Regina': 'CA',
  'America/Montreal': 'CA',
  'America/Moncton': 'CA',
  'America/Iqaluit': 'CA',
  'America/Thunder_Bay': 'CA',
  'America/Whitehorse': 'CA',
  'America/Yellowknife': 'CA',
  'America/Dawson': 'CA',
  'America/Dawson_Creek': 'CA',
  'America/Glace_Bay': 'CA',
  'America/Goose_Bay': 'CA',
  'America/Rankin_Inlet': 'CA',
  'America/Resolute': 'CA',
  'America/Swift_Current': 'CA',
  'America/Cambridge_Bay': 'CA',
  'America/Creston': 'CA',
  'America/Fort_Nelson': 'CA',
  'America/Pangnirtung': 'CA',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Phoenix': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
  'America/Detroit': 'US',
  'America/Indiana/Indianapolis': 'US',
  'America/Boise': 'US',
  'America/Juneau': 'US',
  'America/Sitka': 'US',
  'America/Adak': 'US',
  'America/Nome': 'US',
  'America/Yakutat': 'US',
  'America/Menominee': 'US',
  'America/Kentucky/Louisville': 'US',
  'Europe/London': 'GB',
  'Europe/Berlin': 'DE',
  'Europe/Paris': 'FR',
  'Europe/Madrid': 'ES',
  'Europe/Rome': 'IT',
  'Europe/Amsterdam': 'NL',
  'Europe/Brussels': 'BE',
  'Europe/Vienna': 'AT',
  'Europe/Lisbon': 'PT',
  'Europe/Dublin': 'IE',
  'Europe/Helsinki': 'FI',
  'Europe/Athens': 'GR',
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU',
  'Australia/Adelaide': 'AU',
  'Australia/Hobart': 'AU',
  'Australia/Darwin': 'AU',
  'Pacific/Auckland': 'NZ',
  'America/Mexico_City': 'MX',
  'America/Cancun': 'MX',
  'America/Tijuana': 'MX',
  'America/Merida': 'MX',
  'America/Monterrey': 'MX',
  'America/Hermosillo': 'MX',
  'America/Mazatlan': 'MX',
  'America/Chihuahua': 'MX',
  'America/Sao_Paulo': 'BR',
  'America/Fortaleza': 'BR',
  'America/Recife': 'BR',
  'America/Bahia': 'BR',
  'America/Belem': 'BR',
  'America/Manaus': 'BR',
  'America/Bogota': 'CO',
  'America/Argentina/Buenos_Aires': 'AR',
  'America/Argentina/Cordoba': 'AR',
  'America/Santiago': 'CL',
  'America/Lima': 'PE'
};
interface CurrencyContextValue {
  currency: CurrencyInfo;
  setCurrencyCode: (code: string) => void;
  formatPrice: (usdAmount: number) => string;
  formatPriceRaw: (usdAmount: number) => string;
  currencies: typeof CURRENCIES;
  isDetecting: boolean;
}
const CurrencyContext = createContext<CurrencyContextValue | null>(null);
function detectCurrencyFromLocale(): string | null {
  try {
    const languages = navigator.languages || [navigator.language || 'en-US'];
    for (const lang of languages) {
      const parts = lang.split('-');
      const country = parts[1]?.toUpperCase();
      if (country && COUNTRY_TO_CURRENCY[country]) {
        return COUNTRY_TO_CURRENCY[country];
      }
    }
  } catch {
    // ignore
  }
  return null;
}
function detectCurrencyFromTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const country = TZ_COUNTRY_MAP[tz];
    if (country && COUNTRY_TO_CURRENCY[country]) {
      return COUNTRY_TO_CURRENCY[country];
    }
  } catch {
    // ignore
  }
  return null;
}
export function CurrencyProvider({
  children
}: {
  children: ReactNode;
}) {
  const [currencyCode, setCurrencyCode] = useState<string>(() => {
    // Run synchronous detection immediately for initial render
    const fromLocale = detectCurrencyFromLocale();
    if (fromLocale) return fromLocale;
    const fromTz = detectCurrencyFromTimezone();
    if (fromTz) return fromTz;
    return 'USD';
  });
  const [isDetecting, setIsDetecting] = useState<boolean>(() => {
    // If we already detected from locale or timezone, no need to detect further
    const fromLocale = detectCurrencyFromLocale();
    if (fromLocale) return false;
    const fromTz = detectCurrencyFromTimezone();
    if (fromTz) return false;
    return true;
  });
  useEffect(() => {
    // If already detected synchronously, skip async detection
    if (!isDetecting) return;
    // Strategy 3: Geo-IP detection as last resort
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const tryGeoIP = async () => {
      const services = [{
        url: 'https://ipapi.co/json/',
        getCountry: (d: any) => d?.country_code
      }, {
        url: 'https://ipwho.is/',
        getCountry: (d: any) => d?.country_code
      }, {
        url: 'https://freeipapi.com/api/json',
        getCountry: (d: any) => d?.countryCode
      }];
      for (const service of services) {
        try {
          const res = await fetch(service.url, {
            signal: controller.signal
          });
          const data = await res.json();
          const country = service.getCountry(data);
          if (country && COUNTRY_TO_CURRENCY[country]) {
            setCurrencyCode(COUNTRY_TO_CURRENCY[country]);
            return;
          }
        } catch {
          continue;
        }
      }
    };
    tryGeoIP().finally(() => {
      clearTimeout(timeout);
      setIsDetecting(false);
    });
    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [isDetecting]);
  const currency = CURRENCIES[currencyCode] || CURRENCIES.USD;
  const formatPrice = (usdAmount: number): string => {
    const converted = usdAmount * currency.rate;
    try {
      return new Intl.NumberFormat(currency.locale, {
        style: 'currency',
        currency: currency.code,
        minimumFractionDigits: converted >= 100 ? 0 : 2,
        maximumFractionDigits: converted >= 100 ? 0 : 2
      }).format(converted);
    } catch {
      return `${currency.symbol}${converted.toFixed(2)}`;
    }
  };
  const formatPriceRaw = (usdAmount: number): string => {
    const converted = usdAmount * currency.rate;
    if (converted >= 100) return Math.round(converted).toLocaleString(currency.locale);
    return converted.toFixed(2);
  };
  return <CurrencyContext.Provider value={{
    currency,
    setCurrencyCode: (code: string) => {
      if (CURRENCIES[code]) setCurrencyCode(code);
    },
    formatPrice,
    formatPriceRaw,
    currencies: CURRENCIES,
    isDetecting
  }} data-id="element-249">
      {children}
    </CurrencyContext.Provider>;
}
export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
}